// ===========================================================================
// Charge Capture — Datos mock para el prototipo
//
// Los datos de este archivo simulan la respuesta del Recommendation Engine
// (N.1), el detector de Missing Charges (N.3) y las métricas (Sección 5.1 del
// doc). Cuando el backend esté listo, este archivo se reemplaza por
// llamados a `/api/charges/*` (ver Sección 4.2 del doc).
// ===========================================================================

// ---------------------------------------------------------------------------
// Charges en la cola del coder (N.1 + workspace)
// ---------------------------------------------------------------------------
export const MOCK_CHARGES = [
  {
    chargeId: "CHG-2026-00187",
    cipId: "AB1234567C",
    patientName: "María García",
    patientAge: 58,
    patientGender: "F",
    dateOfService: "2026-05-26",
    providerNpi: "1234567890",
    providerName: "Dr. Pérez",
    visitType: "Annual Wellness Visit",
    placeOfService: "11 — Office",
    status: "RECOMMENDED",
    confidenceOverall: 92,
    hccsCaptured: 2,
    careGapsClosed: 2,
    estimatedRaf: 0.633,
    estimatedRevenue: 425.50,
    clinicalNote: `S: Patient is a 58 y/o female with T2DM, HTN, CHF presenting for AWV. No new complaints. Reports good med adherence. Denies chest pain, SOB at rest, lower extremity edema. Energy good.

O: Vitals: BP 128/78, HR 72, RR 16, T 36.7°C, BMI 31.2.
Labs (2026-05-15): A1c 7.8% (improved from 8.3), eGFR 45 mL/min/1.73m², UACR 65 mg/g, LDL 92, lipid panel WNL.
Echo (2026-03-10): EF 55%, mild diastolic dysfunction.

A/P:
1. T2DM with CKD stage 3a — A1c improving on current regimen. Continue metformin 1000mg BID and lisinopril 20mg daily. Order repeat A1c in 3 months.
2. CKD stage 3a, eGFR 45 — secondary to diabetes. Monitor renal function quarterly. Avoid NSAIDs. Nephrology referral on file.
3. Chronic diastolic CHF, stable, NYHA II — EF 55%. Continue lisinopril, furosemide 20mg daily. Daily weights, low-sodium diet.
4. HTN, well controlled — continue current meds.
5. Long-term insulin use — bedtime glargine 18 units.
6. Depression, recurrent — patient reports mood stable but did not complete PHQ-9 today. Plan: re-screen at next visit.

Vaccines administered today: Influenza quadrivalent IM.

Plan: F/U in 3 months. Continue current meds. Mammography overdue — referral placed.

[signed: Dr. Pérez, 2026-05-26 14:32]`,
    diagnoses: [
      {
        id: "dx-1",
        icd10: "E11.22",
        description: "Type 2 diabetes mellitus with diabetic chronic kidney disease",
        rank: 1,
        source: "RECOMMENDED",
        confidence: "HIGH",
        hccCode: "HCC 18",
        rafContribution: 0.302,
        codingOpportunityId: 45821,
        meatEvidence: 'Documented: "T2DM with CKD stage 3a, A1c 7.8%". MEAT met: Monitor (eGFR), Treat (metformin, lisinopril). Closes Coding Opportunity #45821.',
        accepted: true,
      },
      {
        id: "dx-2",
        icd10: "N18.31",
        description: "Chronic kidney disease, stage 3a",
        rank: 2,
        source: "RECOMMENDED",
        confidence: "HIGH",
        hccCode: null,
        rafContribution: 0.069,
        codingOpportunityId: null,
        meatEvidence: 'Documented: "CKD stage 3a, eGFR 45". Supported by lab from 2026-05-15.',
        accepted: true,
      },
      {
        id: "dx-3",
        icd10: "I50.32",
        description: "Chronic diastolic (congestive) heart failure",
        rank: 3,
        source: "RECOMMENDED",
        confidence: "HIGH",
        hccCode: "HCC 138",
        rafContribution: 0.331,
        codingOpportunityId: 45822,
        meatEvidence: 'Documented: "Stable diastolic CHF, EF 55%". MEAT met: Monitor (echo), Treat (lisinopril, furosemide). Closes Coding Opportunity #45822.',
        accepted: true,
      },
      {
        id: "dx-4",
        icd10: "F33.1",
        description: "Major depressive disorder, recurrent, moderate",
        rank: 4,
        source: "RECOMMENDED",
        confidence: "MEDIUM",
        hccCode: null,
        rafContribution: 0.0,
        codingOpportunityId: null,
        meatEvidence: 'Mentioned but MEAT incomplete (no PHQ-9 score documented today). Suggestion: query provider to confirm evaluation.',
        accepted: false,
        requiresQuery: true,
      },
      {
        id: "dx-5",
        icd10: "Z79.4",
        description: "Long-term (current) use of insulin",
        rank: 5,
        source: "RECOMMENDED",
        confidence: "HIGH",
        hccCode: null,
        rafContribution: 0.0,
        codingOpportunityId: null,
        meatEvidence: 'Documented: "Long-term insulin use — bedtime glargine 18 units".',
        accepted: true,
      },
    ],
    procedures: [
      {
        id: "cpt-1",
        cpt: "G0439",
        description: "Annual Wellness Visit, subsequent",
        modifiers: "",
        units: 1,
        linkedIcd10: "Z00.00",
        source: "RECOMMENDED",
        confidence: "HIGH",
        careGapId: null,
        evidence: 'Visit type = AWV. Last AWV: 2025-05-20 (>12 months ago).',
        accepted: true,
      },
      {
        id: "cpt-2",
        cpt: "90686",
        description: "Influenza virus vaccine, quadrivalent, IM",
        modifiers: "",
        units: 1,
        linkedIcd10: "Z23",
        source: "RECOMMENDED",
        confidence: "HIGH",
        careGapId: 7821,
        evidence: 'Documented: "Influenza vaccine administered". Closes Care Gap (Influenza).',
        accepted: true,
      },
      {
        id: "cpt-3",
        cpt: "36415",
        description: "Routine venipuncture (collection of venous blood)",
        modifiers: "",
        units: 1,
        linkedIcd10: "E11.22",
        source: "RECOMMENDED",
        confidence: "HIGH",
        careGapId: null,
        evidence: 'Documented: "Labs ordered: A1c, BMP".',
        accepted: true,
      },
    ],
    validations: [
      {
        id: "val-1",
        type: "MEDICAL_NECESSITY",
        result: "PASS",
        message: "All CPTs justified by linked ICD-10 codes.",
      },
      {
        id: "val-2",
        type: "NCCI",
        result: "PASS",
        message: "No NCCI bundling conflicts detected.",
      },
      {
        id: "val-3",
        type: "MODIFIER",
        result: "PASS",
        message: "Modifiers correct for E&M + procedure same-day combination.",
      },
      {
        id: "val-4",
        type: "SPECIFICITY",
        result: "WARNING",
        message: "E11.22 selected (max specificity). Provider's historical pattern: E11.9. Capture improves RAF by +0.197 (~$140/month additional premium).",
      },
      {
        id: "val-5",
        type: "CARE_GAP",
        result: "WARNING",
        message: "Patient still has open care gap: Diabetic Retinal Exam (last 2024). Consider referral.",
      },
      {
        id: "val-6",
        type: "HCC_CAPTURE",
        result: "PASS",
        message: "Captured 2 HCC opportunities (HCC 18, HCC 138). 0 HCC opportunities remain open for this patient.",
      },
    ],
  },
  {
    chargeId: "CHG-2026-00188",
    cipId: "DE9876543F",
    patientName: "José Torres",
    patientAge: 67,
    patientGender: "M",
    dateOfService: "2026-05-26",
    providerNpi: "1234567890",
    providerName: "Dr. Pérez",
    visitType: "Follow-up Exam",
    placeOfService: "11 — Office",
    status: "RECOMMENDED",
    confidenceOverall: 88,
    hccsCaptured: 1,
    careGapsClosed: 0,
    estimatedRaf: 0.395,
    estimatedRevenue: 215.00,
    clinicalNote: `S: 67 y/o male, F/U for COPD, HTN, hyperlipidemia. Mild exertional dyspnea, denies cough or wheezing. Adherent to inhalers.

O: BP 138/82, SpO2 95% on RA, lungs with prolonged expiration, no wheezing today. Recent PFTs: FEV1 65% predicted, GOLD stage 2.

A/P:
1. COPD, moderate (GOLD 2) — continue tiotropium daily, albuterol PRN. Pneumococcal vaccine up to date.
2. Essential HTN — slightly elevated today, will recheck in 2 weeks.
3. Mixed hyperlipidemia — continue atorvastatin 40mg.

Plan: F/U 4 weeks for BP recheck.
[signed: Dr. Pérez, 2026-05-26 09:15]`,
    diagnoses: [
      { id: "dx-1", icd10: "J44.1", description: "COPD with acute exacerbation", rank: 1, source: "RECOMMENDED", confidence: "MEDIUM", hccCode: "HCC 111", rafContribution: 0.335, codingOpportunityId: 45901, meatEvidence: 'Documented as moderate COPD (GOLD 2). MEAT met: Monitor (PFTs), Treat (tiotropium, albuterol).', accepted: true },
      { id: "dx-2", icd10: "I10", description: "Essential (primary) hypertension", rank: 2, source: "RECOMMENDED", confidence: "HIGH", hccCode: null, rafContribution: 0.0, codingOpportunityId: null, meatEvidence: 'Documented and treated.', accepted: true },
      { id: "dx-3", icd10: "E78.2", description: "Mixed hyperlipidemia", rank: 3, source: "RECOMMENDED", confidence: "HIGH", hccCode: null, rafContribution: 0.06, codingOpportunityId: null, meatEvidence: 'Treated with atorvastatin 40mg.', accepted: true },
    ],
    procedures: [
      { id: "cpt-1", cpt: "99214", description: "Office/outpatient visit, established patient, moderate", modifiers: "", units: 1, linkedIcd10: "J44.1", source: "RECOMMENDED", confidence: "HIGH", careGapId: null, evidence: 'Established patient, 3 stable chronic conditions managed.', accepted: true },
    ],
    validations: [
      { id: "val-1", type: "MEDICAL_NECESSITY", result: "PASS", message: "All CPTs justified by linked ICD-10 codes." },
      { id: "val-2", type: "SPECIFICITY", result: "WARNING", message: "J44.1 requires documented exacerbation. Note suggests stable disease — consider J44.9 instead." },
    ],
  },
  {
    chargeId: "CHG-2026-00189",
    cipId: "GH4567891J",
    patientName: "Ana Ruiz",
    patientAge: 71,
    patientGender: "F",
    dateOfService: "2026-05-25",
    providerNpi: "9876543210",
    providerName: "Dr. López",
    visitType: "Sick Visit",
    placeOfService: "11 — Office",
    status: "IN_REVIEW",
    confidenceOverall: 75,
    hccsCaptured: 0,
    careGapsClosed: 1,
    estimatedRaf: 0.0,
    estimatedRevenue: 165.00,
    clinicalNote: `S: 71 y/o female, c/o productive cough x 5 days, low-grade fever, no SOB. No sick contacts.

O: T 37.8°C, lungs with right basal crackles, SpO2 96% RA.

A/P:
1. Acute bronchitis — likely viral. Symptomatic treatment: dextromethorphan, fluids, rest.

Plan: F/U if symptoms worsen or persist >7 days.
[signed: Dr. López, 2026-05-25 11:00]`,
    diagnoses: [
      { id: "dx-1", icd10: "J20.9", description: "Acute bronchitis, unspecified", rank: 1, source: "RECOMMENDED", confidence: "HIGH", hccCode: null, rafContribution: 0.0, codingOpportunityId: null, meatEvidence: 'Documented and treated symptomatically.', accepted: true },
    ],
    procedures: [
      { id: "cpt-1", cpt: "99213", description: "Office/outpatient visit, established patient, low complexity", modifiers: "", units: 1, linkedIcd10: "J20.9", source: "RECOMMENDED", confidence: "HIGH", careGapId: null, evidence: 'Established patient, single acute condition, low complexity decision-making.', accepted: true },
    ],
    validations: [
      { id: "val-1", type: "MEDICAL_NECESSITY", result: "PASS", message: "CPT 99213 justified by acute bronchitis diagnosis." },
      { id: "val-2", type: "CARE_GAP", result: "PASS", message: "No applicable care gaps for this visit type." },
    ],
  },
];

// ---------------------------------------------------------------------------
// Missing Charges (N.3 — Sección 3.3 del doc)
// ---------------------------------------------------------------------------
export const MOCK_MISSING_CHARGES = {
  summary: {
    appointmentsCompleted: 247,
    chargesProcessed: 235,
    missingCount: 12,
    missingRate: 4.8,
    revenueAtRisk: 3800,
    monthlyTrendPct: 3.2,
    monthlyTargetPct: 2.0,
    monthlyRevenueAtRisk: 45000,
  },
  items: [
    { id: "miss-1", patientName: "María García",  cipId: "AB1234567C", providerName: "Dr. Pérez",   dateOfService: "2026-05-22", hoursElapsed: 96, severity: "CRITICAL" },
    { id: "miss-2", patientName: "José Torres",   cipId: "DE9876543F", providerName: "Dr. Pérez",   dateOfService: "2026-05-22", hoursElapsed: 94, severity: "CRITICAL" },
    { id: "miss-3", patientName: "Ana Ruiz",      cipId: "GH4567891J", providerName: "Dr. López",   dateOfService: "2026-05-23", hoursElapsed: 68, severity: "WARNING" },
    { id: "miss-4", patientName: "Carlos Mendez", cipId: "KL2345678M", providerName: "Dr. Sánchez", dateOfService: "2026-05-24", hoursElapsed: 44, severity: "REVIEW" },
    { id: "miss-5", patientName: "Lucia Romero",  cipId: "NO3456789P", providerName: "Dr. Rivera",  dateOfService: "2026-05-24", hoursElapsed: 42, severity: "REVIEW" },
    { id: "miss-6", patientName: "Pedro Castro",  cipId: "QR4567890S", providerName: "Dr. Vega",    dateOfService: "2026-05-24", hoursElapsed: 40, severity: "REVIEW" },
    { id: "miss-7", patientName: "Sara Jiménez",  cipId: "TU5678901V", providerName: "Dr. Pérez",   dateOfService: "2026-05-25", hoursElapsed: 36, severity: "REVIEW" },
    { id: "miss-8", patientName: "Hugo Morales",  cipId: "WX6789012Y", providerName: "Dr. López",   dateOfService: "2026-05-25", hoursElapsed: 34, severity: "REVIEW" },
    { id: "miss-9", patientName: "Elena Vargas",  cipId: "ZA7890123B", providerName: "Dr. Sánchez", dateOfService: "2026-05-25", hoursElapsed: 32, severity: "REVIEW" },
    { id: "miss-10", patientName: "Diego Ortiz",  cipId: "CD8901234E", providerName: "Dr. Vega",    dateOfService: "2026-05-25", hoursElapsed: 30, severity: "REVIEW" },
    { id: "miss-11", patientName: "Rosa Núñez",   cipId: "FG9012345H", providerName: "Dr. Rivera",  dateOfService: "2026-05-25", hoursElapsed: 28, severity: "REVIEW" },
    { id: "miss-12", patientName: "Iván Pacheco", cipId: "IJ0123456K", providerName: "Dr. López",   dateOfService: "2026-05-25", hoursElapsed: 26, severity: "REVIEW" },
  ],
};

// ---------------------------------------------------------------------------
// Métricas mensuales (Sección 5.1 — Demo 3 del doc)
// ---------------------------------------------------------------------------
export const MOCK_METRICS = {
  monthLabel: "May 2026",
  captureRate: 96.8,
  captureRateTarget: 98.0,
  hccsAdditionalCaptured: 234,
  specificityImprovements: 156,
  revenueImpact: 187000,
  byProvider: [
    { name: "Dr. López",   rate: 99.2, status: "ok" },
    { name: "Dr. Sánchez", rate: 97.8, status: "ok" },
    { name: "Dr. Pérez",   rate: 94.5, status: "coaching" },
    { name: "Dr. Rivera",  rate: 92.1, status: "coaching" },
    { name: "Dr. Vega",    rate: 88.7, status: "review" },
  ],
  alerts: {
    specificityPending: 23,
    missingChargesOver24h: 12,
    queriesPendingResponse: 3,
  },
};

// Helper para buscar un charge por id (usado por el workspace).
export function findMockChargeById(chargeId) {
  return MOCK_CHARGES.find((c) => c.chargeId === chargeId) || null;
}

// ---------------------------------------------------------------------------
// Catálogos de búsqueda para ingreso manual (lookupproccode / lookupdiagcode).
// savecharges necesita el ID INTERNO del CPT (procId), no el código — por eso
// el catálogo de procedimientos lleva ambos.
// ---------------------------------------------------------------------------
export const MOCK_PROC_CATALOG = [
  { procId: 70011, cpt: "99202", description: "Office visit, new patient, straightforward" },
  { procId: 70012, cpt: "99203", description: "Office visit, new patient, low complexity" },
  { procId: 70013, cpt: "99204", description: "Office visit, new patient, moderate complexity" },
  { procId: 70021, cpt: "99212", description: "Office visit, established, straightforward" },
  { procId: 70022, cpt: "99213", description: "Office visit, established, low complexity" },
  { procId: 70023, cpt: "99214", description: "Office visit, established, moderate complexity" },
  { procId: 70024, cpt: "99215", description: "Office visit, established, high complexity" },
  { procId: 70031, cpt: "G0438", description: "Annual Wellness Visit, initial" },
  { procId: 70032, cpt: "G0439", description: "Annual Wellness Visit, subsequent" },
  { procId: 70041, cpt: "99495", description: "TCM, moderate complexity (14-day)" },
  { procId: 70042, cpt: "99496", description: "TCM, high complexity (7-day)" },
  { procId: 70051, cpt: "90686", description: "Influenza virus vaccine, quadrivalent, IM" },
  { procId: 70052, cpt: "36415", description: "Routine venipuncture" },
  { procId: 70061, cpt: "96127", description: "Brief behavioral assessment (PHQ)" },
  { procId: 70062, cpt: "93000", description: "Electrocardiogram, complete" },
  { procId: 70063, cpt: "81002", description: "Urinalysis, non-automated, without microscopy" },
];

export const MOCK_DIAG_CATALOG = [
  { icd10: "E11.9",  description: "Type 2 diabetes mellitus without complications", hccCode: "HCC 19" },
  { icd10: "E11.22", description: "T2DM with diabetic chronic kidney disease", hccCode: "HCC 18" },
  { icd10: "E11.42", description: "T2DM with diabetic polyneuropathy", hccCode: "HCC 18" },
  { icd10: "I10",    description: "Essential (primary) hypertension", hccCode: null },
  { icd10: "I50.32", description: "Chronic diastolic (congestive) heart failure", hccCode: "HCC 138" },
  { icd10: "I48.91", description: "Unspecified atrial fibrillation", hccCode: "HCC 96" },
  { icd10: "N18.31", description: "Chronic kidney disease, stage 3a", hccCode: null },
  { icd10: "J44.1",  description: "COPD with acute exacerbation", hccCode: "HCC 111" },
  { icd10: "J44.9",  description: "COPD, unspecified", hccCode: "HCC 111" },
  { icd10: "J20.9",  description: "Acute bronchitis, unspecified", hccCode: null },
  { icd10: "F33.1",  description: "Major depressive disorder, recurrent, moderate", hccCode: null },
  { icd10: "E78.2",  description: "Mixed hyperlipidemia", hccCode: null },
  { icd10: "Z79.4",  description: "Long-term (current) use of insulin", hccCode: null },
  { icd10: "Z23",    description: "Encounter for immunization", hccCode: null },
  { icd10: "Z00.00", description: "General adult medical exam without abnormal findings", hccCode: null },
  { icd10: "M79.7",  description: "Fibromyalgia", hccCode: null },
];

// ---------------------------------------------------------------------------
// Observaciones (chat coder ↔ médico) sembradas por charge. Simulan el hilo
// de queries que hoy ocurre por teléfono/email y que el backend persistirá en
// billing.TAB_CHARGE_OBSERVATIONS.
// ---------------------------------------------------------------------------
export const MOCK_OBSERVATIONS = {
  "CHG-2026-00187": [
    {
      id: "obs-1",
      author: "coder",
      authorName: "R. Méndez (Coder)",
      text: "Dr. Pérez — the note mentions recurrent depression but no PHQ-9 score was documented today. Can you confirm the evaluation so we can support F33.1 with MEAT?",
      at: "2026-05-26T15:10:00Z",
    },
    {
      id: "obs-2",
      author: "provider",
      authorName: "Dr. Pérez",
      text: "Correct, the PHQ-9 was not completed today — patient declined. Please leave F33.1 off this claim; I will re-screen at the 3-month follow-up.",
      at: "2026-05-26T16:42:00Z",
    },
  ],
  "CHG-2026-00188": [
    {
      id: "obs-1",
      author: "coder",
      authorName: "R. Méndez (Coder)",
      text: "The note describes stable COPD (GOLD 2) but J44.1 requires a documented exacerbation. Should I switch to J44.9?",
      at: "2026-05-26T10:05:00Z",
    },
  ],
};
